import React, { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { toast } from "sonner";
import {
  Plus, X, Loader2, Play, Crown, Globe, ArrowUpRight, Save, ArrowUp, ArrowDown, Download,
} from "lucide-react";
import { rowsToCsv, downloadCsv, slugForFilename, todayStamp } from "../../lib/csv";
import { ResponsiveContainer, LineChart, Line, YAxis } from "recharts";
import CompetitorHistoryGraphs from "./CompetitorHistoryGraphs";
import TabFreshnessNote from "./TabFreshnessNote";
import ContentGapCard from "./ContentGapCard";
import CompetitorSuggestions from "./CompetitorSuggestions";

function fmt(n) {
  if (n == null) return "—";
  if (n >= 1e9) return (n / 1e9).toFixed(1) + "B";
  if (n >= 1e6) return (n / 1e6).toFixed(1) + "M";
  if (n >= 1e3) return (n / 1e3).toFixed(1) + "K";
  if (typeof n === "number" && !Number.isInteger(n)) return n.toFixed(0);
  return String(n);
}

function normDomain(s) {
  return (s || "").trim().replace(/^https?:\/\//, "").replace(/\/$/, "").toLowerCase();
}

export default function CompetitorsTab({ client, reloadClient }) {
  const { isAdmin } = useAuth();
  const [input, setInput] = useState("");
  const [savingComp, setSavingComp] = useState(false);
  const [comparison, setComparison] = useState(null);
  const [running, setRunning] = useState(false);
  const [snapshots, setSnapshots] = useState([]);
  const [savingSnap, setSavingSnap] = useState(false);

  const loadSnapshots = async () => {
    try {
      const { data } = await api.get(`/clients/${client.id}/competitors/snapshots?limit=50`);
      setSnapshots(data.snapshots || []);
    } catch (e) {
      setSnapshots([]);
    }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      // Fire snapshot list + latest comparison in parallel — they're independent.
      try {
        const [snapRes, latestRes] = await Promise.all([
          api.get(`/clients/${client.id}/competitors/snapshots?limit=50`),
          api.get(`/clients/${client.id}/competitors/latest`).catch(() => ({ data: { status: "empty" } })),
        ]);
        if (!alive) return;
        setSnapshots(snapRes.data.snapshots || []);
        if (latestRes.data?.status === "ok" && latestRes.data.comparison) {
          setComparison(latestRes.data.comparison);
        }
      } catch (e) {
        if (alive) setSnapshots([]);
      }
    })();
    return () => { alive = false; };
  }, [client.id]);

  const competitors = client.competitors || [];

  const addCompetitor = async (raw) => {
    const d = normDomain(raw);
    if (!d) return;
    if (d === normDomain(client.domain)) {
      toast.error("That's the client's own domain.");
      return;
    }
    if (competitors.map(normDomain).includes(d)) {
      toast.info("Already in your competitor list.");
      return;
    }
    setSavingComp(true);
    try {
      await api.patch(`/clients/${client.id}`, {
        competitors: [...competitors, d],
      });
      setInput("");
      reloadClient?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add");
    } finally {
      setSavingComp(false);
    }
  };

  const removeCompetitor = async (d) => {
    setSavingComp(true);
    try {
      const next = competitors.filter((c) => normDomain(c) !== normDomain(d));
      await api.patch(`/clients/${client.id}`, { competitors: next });
      // Drop stale comparison column for removed competitor
      if (comparison) {
        setComparison((prev) => prev && {
          ...prev,
          targets: prev.targets.filter((t) => normDomain(t.domain) !== normDomain(d) || t.is_client),
        });
      }
      reloadClient?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove");
    } finally {
      setSavingComp(false);
    }
  };

  const runCompare = async () => {
    if (!client.keywords?.length) {
      toast.error("Add tracked keywords to the client first.");
      return;
    }
    if (!competitors.length) {
      toast.error("Add at least one competitor to compare.");
      return;
    }
    setRunning(true);
    try {
      const { data } = await api.post(`/clients/${client.id}/competitors/compare`);
      setComparison(data);
      toast.success(`Compared ${data.targets.length} domains across ${data.keywords.length} keyword${data.keywords.length > 1 ? "s" : ""}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Comparison failed");
    } finally {
      setRunning(false);
    }
  };

  const saveSnapshot = async () => {
    if (!comparison) return;
    setSavingSnap(true);
    try {
      await api.post(`/clients/${client.id}/competitors/snapshot`, comparison);
      toast.success("Snapshot saved · stored for 12 months.");
      await loadSnapshots();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingSnap(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="competitors-tab">
      <TabFreshnessNote testid="competitors-freshness">
        Comparison snapshots auto-saved monthly on the 1st alongside the digest · Use Save snapshot for on-demand captures.
      </TabFreshnessNote>

      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-[#a0aec0] font-semibold">
            Competitors
          </div>
          <h3 className="font-display text-2xl mt-0.5 text-[#1a202c]">Your competitor set</h3>
          <p className="text-xs text-[#718096] mt-1">
            Add competitor domains and run a side-by-side comparison across tracked keywords + domain metrics.
          </p>
        </div>
        {isAdmin && (
          <button
            onClick={runCompare}
            disabled={client.frozen || running || competitors.length === 0 || (client.keywords?.length || 0) === 0}
            title={client.frozen ? "Client is frozen — unfreeze to pull new data" : undefined}
            data-testid="competitors-run-compare"
            className="btn-primary inline-flex items-center gap-2"
          >
            {running ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
            {running ? "Comparing…" : "Run comparison"}
          </button>
        )}
      </div>

      {/* Suggested competitors (from Local Pack + keyword overlap) */}
      <CompetitorSuggestions client={client} reloadClient={reloadClient} />

      {/* Manage competitors */}
      <div className="card-soft p-5 space-y-3">
        <div className="text-[11px] uppercase tracking-widest text-[#a0aec0] font-semibold">
          Manage
        </div>
        <form
          onSubmit={(e) => { e.preventDefault(); addCompetitor(input); }}
          className="flex gap-2 items-center"
        >
          <div className="flex-1 relative">
            <Globe className="h-3.5 w-3.5 absolute left-3.5 top-1/2 -translate-y-1/2 text-[#a0aec0]" />
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="e.g. competitor.com"
              data-testid="competitor-input"
              disabled={savingComp}
              className="w-full bg-white border border-[#e2e8f0] rounded-xl pl-10 pr-3 py-2.5 text-sm font-mono-tight focus:outline-none focus:border-[#4fd1c5] focus:ring-4 focus:ring-[#4fd1c5]/15 transition-all"
            />
          </div>
          <button
            type="submit"
            disabled={savingComp || !input.trim()}
            data-testid="competitor-add"
            className="btn-primary inline-flex items-center gap-2"
          >
            <Plus className="h-3.5 w-3.5" /> Add
          </button>
        </form>

        {competitors.length === 0 ? (
          <div className="text-xs text-[#a0aec0]">
            No competitors yet. Add up to a handful of domains you want to benchmark against.
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {competitors.map((d) => (
              <div
                key={d}
                data-testid={`competitor-chip-${d}`}
                className="inline-flex items-center gap-2 bg-[#4fd1c5]/10 border border-[#4fd1c5]/25 text-[#234e52] text-xs font-semibold px-3 py-1.5 rounded-full"
              >
                <span className="font-mono-tight">{d}</span>
                <button
                  onClick={() => removeCompetitor(d)}
                  data-testid={`competitor-remove-${d}`}
                  className="h-4 w-4 inline-flex items-center justify-center rounded-full hover:bg-[#c53030] hover:text-white text-[#2c7a7b] transition-colors"
                  title={`Remove ${d}`}
                >
                  <X className="h-3 w-3" strokeWidth={2.6} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Comparison table */}
      {comparison && (
        <ComparisonView
          comparison={comparison}
          clientDomain={client.domain}
          snapshots={snapshots}
          onSaveSnapshot={saveSnapshot}
          savingSnap={savingSnap}
        />
      )}

      {!comparison && competitors.length > 0 && client.keywords?.length > 0 && (
        <div className="card-soft p-6 text-center text-sm text-[#718096]">
          Click <span className="font-semibold text-[#1a202c]">Run comparison</span> to see how
          your client stacks up across {client.keywords.length} keyword{client.keywords.length > 1 ? "s" : ""}.
        </div>
      )}

      {/* Content gap — keywords competitors rank for but client doesn't */}
      <ContentGapCard client={client} reloadClient={reloadClient} />

      {/* Historical performance graphs (auto-tracked weekly) */}
      <CompetitorHistoryGraphs snapshots={snapshots} clientDomain={client.domain} />
    </div>
  );
}

function ComparisonView({ comparison, clientDomain, snapshots, onSaveSnapshot, savingSnap }) {
  const { targets, rows } = comparison;

  const summaryByDomain = useMemo(() => {
    const wins = {};
    for (const t of targets) wins[normDomain(t.domain)] = { wins: 0, top10: 0, ranking: 0 };
    for (const r of rows) {
      let best = null;
      let bestDom = null;
      for (const t of targets) {
        const k = normDomain(t.domain);
        const p = r.positions?.[k];
        if (p != null) {
          wins[k].ranking += 1;
          if (p <= 10) wins[k].top10 += 1;
          if (best == null || p < best) { best = p; bestDom = k; }
        }
      }
      if (bestDom) wins[bestDom].wins += 1;
    }
    return wins;
  }, [targets, rows]);

  // Share-of-voice sparkline data + delta vs previous snapshot
  const sovHistory = useMemo(() => {
    const list = [...(snapshots || [])].reverse(); // oldest -> newest
    return list.map((s) => ({ at: s.at, sov: s.sov || {} }));
  }, [snapshots]);

  const prevSov = snapshots?.[0]?.sov || null;

  // Look up the previous position for delta column
  const prevPositionFor = (kw, domKey, scope) => {
    const prev = snapshots?.[0];
    if (!prev) return null;
    const row = (prev.rows || []).find((r) => r.keyword === kw && (!r.scope || !scope || r.scope === scope));
    if (!row) return null;
    return row.positions?.[domKey] ?? null;
  };

  const exportCsv = () => {
    const metricCols = [
      { header: "Domain", accessor: (t) => t.domain },
      { header: "Is client", accessor: (t) => (t.is_client ? "Y" : "N") },
      { header: "Ranking keywords", accessor: (t) => t.overview?.ranking_keywords ?? "" },
      { header: "Est. traffic", accessor: (t) => (t.overview?.est_traffic != null ? Math.round(t.overview.est_traffic) : "") },
      { header: "Referring domains", accessor: (t) => t.overview?.referring_domains ?? "" },
      { header: "Domain rank", accessor: (t) => t.overview?.rank ?? "" },
      { header: "Domain authority", accessor: (t) => t.overview?.domain_authority ?? "" },
      { header: "Keyword wins", accessor: (t) => summaryByDomain[normDomain(t.domain)]?.wins ?? 0 },
      { header: "Top 10 keywords", accessor: (t) => summaryByDomain[normDomain(t.domain)]?.top10 ?? 0 },
    ];
    const kwCols = [
      { header: "Keyword", accessor: (r) => r.keyword },
      { header: "Scope", accessor: (r) => r.scope || "" },
      ...targets.flatMap((t) => {
        const k = normDomain(t.domain);
        return [
          { header: `${t.domain} position`, accessor: (r) => r.positions?.[k] ?? "" },
          { header: `${t.domain} URL`, accessor: (r) => r.urls?.[k] ?? "" },
          { header: `${t.domain} other SERP features`, accessor: (r) => (r.features?.[k] || []).join(" | ") },
        ];
      }),
    ];
    const csv = [
      '"Domain metrics"',
      rowsToCsv(targets, metricCols),
      "",
      '"Keyword head-to-head"',
      rowsToCsv(rows, kwCols),
    ].join("\n");
    downloadCsv(`${slugForFilename(clientDomain)}-competitor-head-to-head-${todayStamp()}.csv`, csv);
  };

  return (
    <div className="space-y-4" data-testid="comparison-view">
      {/* Save button */}
      <div className="flex items-center justify-end gap-2 -mt-1">
        <button
          onClick={exportCsv}
          data-testid="comparison-export-csv"
          title="Download domain metrics + keyword head-to-head as CSV"
          className="btn-ghost inline-flex items-center gap-2"
        >
          <Download className="h-3.5 w-3.5" />
          Export CSV
        </button>
        <button
          onClick={onSaveSnapshot}
          disabled={savingSnap}
          data-testid="save-comparison-snapshot"
          className="btn-ghost inline-flex items-center gap-2"
        >
          {savingSnap ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
          {savingSnap ? "Saving…" : "Save snapshot"}
        </button>
      </div>

      {/* Domain rank cards with sparkline */}
      <div className="card-soft overflow-x-auto">
        <div className="px-5 py-4 border-b border-[#edf2f7] flex items-end justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-[#a0aec0] font-semibold">
              Domain metrics
            </div>
            <h3 className="font-display text-base mt-0.5 text-[#1a202c]">Side-by-side overview</h3>
          </div>
          <div className="text-[10px] text-[#a0aec0] font-mono-tight">
            {sovHistory.length} snapshot{sovHistory.length === 1 ? "" : "s"} stored (12-month history)
          </div>
        </div>
        <div className="flex gap-4 p-5 min-w-max">
          {targets.map((t) => {
            const k = normDomain(t.domain);
            const s = summaryByDomain[k] || {};
            const isClient = t.is_client;
            // Build sparkline series for this domain's top10 % over time
            const series = sovHistory.map((h) => ({ v: h.sov?.[k]?.top10_pct ?? 0 }));
            // Add the current (in-memory) point at the end so the live snapshot is reflected
            const totalRows = rows.length || 1;
            const currentSov = Math.round(100 * (s.top10 || 0) / totalRows);
            series.push({ v: currentSov });
            const sovDelta = sovHistory.length > 0 ? (currentSov - (sovHistory[sovHistory.length - 1].sov?.[k]?.top10_pct ?? 0)) : null;
            return (
              <div
                key={k}
                data-testid={`compare-target-${k}`}
                className={`min-w-[240px] rounded-2xl p-4 border ${
                  isClient
                    ? "bg-brand text-white border-transparent shadow-[0_8px_18px_-6px_rgba(56,178,172,0.5)]"
                    : "bg-white text-[#1a202c] border-[#e2e8f0]"
                }`}
              >
                <div className="flex items-center gap-2">
                  {isClient && <Crown className="h-3.5 w-3.5" />}
                  <div className="text-[10px] uppercase tracking-widest font-semibold opacity-80">
                    {isClient ? "Your client" : "Competitor"}
                  </div>
                </div>
                <div className="font-mono-tight text-base mt-1 truncate">{t.domain}</div>

                {/* Share of voice */}
                <div className={`mt-3 rounded-xl p-3 ${isClient ? "bg-white/15" : "bg-[#f7fafc]"}`}>
                  <div className="flex items-center justify-between">
                    <div className="text-[10px] uppercase tracking-widest font-semibold opacity-80">
                      Share of voice
                    </div>
                    {sovDelta != null && sovDelta !== 0 && (
                      <span className={`text-[10px] font-bold inline-flex items-center gap-0.5 ${
                        isClient
                          ? "text-white"
                          : sovDelta > 0 ? "text-[#38b2ac]" : "text-[#f56565]"
                      }`}>
                        {sovDelta > 0 ? <ArrowUp className="h-2.5 w-2.5" /> : <ArrowDown className="h-2.5 w-2.5" />}
                        {Math.abs(sovDelta).toFixed(0)}%
                      </span>
                    )}
                  </div>
                  <div className="font-mono-tight text-2xl font-bold mt-0.5">
                    {currentSov}%
                  </div>
                  <div className="h-10 mt-1">
                    {series.length > 1 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={series}>
                          <YAxis domain={[0, 100]} hide />
                          <Line
                            type="monotone"
                            dataKey="v"
                            stroke={isClient ? "#ffffff" : "#38b2ac"}
                            strokeWidth={2}
                            dot={false}
                            isAnimationActive={false}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className={`text-[10px] font-mono-tight pt-3 ${isClient ? "text-white/70" : "text-[#a0aec0]"}`}>
                        Save snapshots to chart history.
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                  <Kv k="Ranking kw" v={fmt(t.overview?.ranking_keywords)} dark={isClient} />
                  <Kv k="Est. traffic" v={fmt(Math.round(t.overview?.est_traffic || 0))} dark={isClient} />
                  <Kv k="Ref. domains" v={fmt(t.overview?.referring_domains)} dark={isClient} />
                  <Kv
                    k={t.overview?.rank != null ? "Domain rank" : "Moz DA"}
                    v={
                      t.overview?.rank != null
                        ? t.overview.rank
                        : (t.overview?.domain_authority != null ? t.overview.domain_authority : "—")
                    }
                    dark={isClient}
                  />
                </div>
                <div className={`mt-3 pt-3 border-t ${isClient ? "border-white/25" : "border-[#edf2f7]"}`}>
                  <div className="flex items-center gap-3 text-xs font-mono-tight">
                    <span>🏆 {s.wins ?? 0}</span>
                    <span>top10 {s.top10 ?? 0}</span>
                    <span>any {s.ranking ?? 0}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Keyword grid */}
      <div className="card-soft overflow-hidden">
        <div className="px-5 py-4 border-b border-[#edf2f7] flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-[#a0aec0] font-semibold">
              Keyword head-to-head
            </div>
            <h3 className="font-display text-base mt-0.5 text-[#1a202c]">Live SERP positions</h3>
          </div>
          {prevSov && (
            <div className="text-[10px] text-[#a0aec0] font-mono-tight">
              Δ vs snapshot {new Date(snapshots[0].at).toLocaleDateString()}
            </div>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#f7fafc] border-b border-[#edf2f7]">
                <th className="text-left text-[10px] uppercase tracking-widest text-[#a0aec0] font-bold px-5 py-3 sticky left-0 bg-[#f7fafc]">
                  Keyword
                </th>
                {targets.map((t) => (
                  <th
                    key={normDomain(t.domain)}
                    className={`text-right text-[10px] uppercase tracking-widest font-bold px-4 py-3 ${
                      t.is_client ? "text-[#2c7a7b]" : "text-[#a0aec0]"
                    }`}
                  >
                    <div className="truncate max-w-[160px] inline-flex items-center gap-1 justify-end">
                      {t.is_client && <Crown className="h-3 w-3" />}
                      {t.domain}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const pairs = targets.map((t) => ({
                  k: normDomain(t.domain),
                  p: r.positions?.[normDomain(t.domain)],
                }));
                const ranked = pairs.filter((x) => x.p != null);
                const best = ranked.length ? Math.min(...ranked.map((x) => x.p)) : null;
                return (
                  <tr key={i} data-testid={`compare-kw-row-${i}`} className="border-b border-[#edf2f7] hover:bg-[#f7fafc]">
                    <td className="px-5 py-3 text-[#1a202c] sticky left-0 bg-white">
                      {r.keyword}
                      {r.scope === "local" && (
                        <span className="ml-2 text-[9px] font-bold uppercase tracking-wider text-[#2c7a7b]" data-testid={`compare-kw-scope-${i}`}>Local</span>
                      )}
                    </td>
                    {targets.map((t) => {
                      const k = normDomain(t.domain);
                      const p = r.positions?.[k];
                      const u = r.urls?.[k];
                      const prevP = prevPositionFor(r.keyword, k, r.scope);
                      const delta = (prevP != null && p != null) ? prevP - p : null;
                      const isBest = best != null && p === best;
                      return (
                        <td
                          key={k}
                          className={`px-4 py-3 text-right font-mono-tight ${
                            isBest ? "text-[#38b2ac] font-bold" : "text-[#4a5568]"
                          }`}
                        >
                          {p != null ? (
                            <div className="inline-flex items-center gap-1.5 justify-end">
                              <a
                                href={u || "#"}
                                target="_blank"
                                rel="noreferrer"
                                onClick={(e) => !u && e.preventDefault()}
                                className="inline-flex items-center gap-1"
                                title={u || "—"}
                              >
                                {p}
                                {u && <ArrowUpRight className="h-3 w-3 opacity-60" />}
                              </a>
                              {delta != null && delta !== 0 && (
                                <span className={`text-[9px] font-bold ${delta > 0 ? "text-[#38b2ac]" : "text-[#f56565]"}`}>
                                  {delta > 0 ? "+" : ""}{delta}
                                </span>
                              )}
                            </div>
                          ) : r.features?.[k]?.length ? (
                            <div className="inline-flex flex-wrap gap-1 justify-end" data-testid={`compare-feature-badges-${i}-${k}`}>
                              {r.features[k].map((f) => (
                                <span
                                  key={f}
                                  title={`Appears in ${f} but not in the organic results`}
                                  className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border border-[#e2e8f0] bg-[#f7fafc] text-[#718096] font-sans"
                                >
                                  {f}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-[#cbd5e0]">—</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Kv({ k, v, dark }) {
  return (
    <div>
      <div className={`text-[9px] uppercase tracking-widest font-semibold ${dark ? "text-white/70" : "text-[#a0aec0]"}`}>
        {k}
      </div>
      <div className="font-mono-tight text-sm mt-0.5">{v}</div>
    </div>
  );
}
